import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  DollarSign, Search, Filter, Plus, Edit, Trash2,
  ArrowUpRight, Globe, AlertCircle, Settings,
  Calendar, Clock, CheckCircle, RefreshCw, X,
  ChevronDown, Building2, MapPin, Calculator,
  CreditCard, Percent, Lock, ChevronRight,
  FileText, BarChart2, Loader2
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import AdminHeader from "../../Headers/AdminHeader";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { fetchCurrencies, deleteCurrency, setCurrentCurrency } from "../../redux/Slices/CurrencySlice";
import { getAllRegionSettings } from "../../redux/Actions/RegionSettingsAction";
import { 
  updateCurrencyExchangeRate, 
  updateAllCurrencyExchangeRates,
  getExchangeRateApiStatus
} from "../../redux/Actions/CurrencyActions";
import { Currency, CurrencyStatus } from "../../modules/shared/model";
import CurrencyActionModal from "./CurrencyActionModal";
import { GlobalService } from "../../modules";

const CurrencySettings = () => {
  const dispatch = useAppDispatch();
  const { 
    items: currencies, 
    loading, 
    error, 
    totalCount,
    hasNextPage,
    hasPreviousPage,
    page: currentServerPage,
    limit: serverLimit
  } = useAppSelector((state) => state.currencies);
  const { regionSettings, loading: regionsLoading } = useAppSelector((state) => state.regionSettings);
  
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [selectedRegion, setSelectedRegion] = useState("all");
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState<Currency | null>(null);
  const [modalMode, setModalMode] = useState<"create" | "edit" | "view">("create");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [updatingRates, setUpdatingRates] = useState<{ [key: string]: boolean }>({});
  const [updatingAllRates, setUpdatingAllRates] = useState(false);
  const [apiStatus, setApiStatus] = useState<any>(null);
  const searchDebounceTimer = useRef<NodeJS.Timeout | null>(null);

  // Debounce search query - trigger backend search after user stops typing
  useEffect(() => {
    // Clear existing timer
    if (searchDebounceTimer.current) {
      clearTimeout(searchDebounceTimer.current);
    }

    // Set new timer to update debounced search after 600ms
    searchDebounceTimer.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setCurrentPage(1); // Reset to first page when search changes
    }, 600);

    // Cleanup timer on unmount or when searchQuery changes
    return () => {
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current);
      }
    };
  }, [searchQuery]);

  // Load regions on component mount
  useEffect(() => {
    dispatch(getAllRegionSettings({ page: 1, limit: 100 }));
    checkApiStatus();
  }, [dispatch]);

  // Fetch currencies when page, limit, or debounced search changes
  useEffect(() => {
    const queryParams: any = {
      page: currentPage,
      limit: itemsPerPage
    };

    // Add search query if present
    if (debouncedSearchQuery.trim()) {
      queryParams.name = debouncedSearchQuery.trim();
    }

    dispatch(fetchCurrencies(queryParams));
  }, [dispatch, currentPage, itemsPerPage, debouncedSearchQuery]);

  // Check ExchangeRate-API status
  const checkApiStatus = async () => {
    try {
      const result = await dispatch(getExchangeRateApiStatus({ isShowLoader: false }));
      if (getExchangeRateApiStatus.fulfilled.match(result)) {
        setApiStatus(result.payload);
      }
    } catch (error) {
      console.error("Failed to check API status:", error);
    }
  };

  // Update single currency exchange rate
  const handleUpdateRate = async (currencyId: string) => {
    setUpdatingRates(prev => ({ ...prev, [currencyId]: true }));
    
    try {
      const result = await dispatch(updateCurrencyExchangeRate({ currencyId, isShowLoader: false }));
      
      if (updateCurrencyExchangeRate.fulfilled.match(result)) {
        GlobalService.showToast("success", "Success", "Exchange rate updated successfully");
        
        // Refresh currencies list with current search
        const queryParams: any = {
          page: currentPage,
          limit: itemsPerPage
        };
        if (debouncedSearchQuery.trim()) {
          queryParams.name = debouncedSearchQuery.trim();
        }
        dispatch(fetchCurrencies(queryParams));
      } else {
        const error = result.payload as string;
        GlobalService.showToast("error", "Error", error || "Failed to update exchange rate");
      }
    } catch (error: any) {
      console.error("Failed to update exchange rate:", error);
      GlobalService.showToast("error", "Error", "Failed to update exchange rate");
    } finally {
      setUpdatingRates(prev => ({ ...prev, [currencyId]: false }));
    }
  };

  // Update all currency exchange rates
  const handleUpdateAllRates = async () => {
    setUpdatingAllRates(true);
    
    try {
      const result = await dispatch(updateAllCurrencyExchangeRates({ isShowLoader: true }));
      
      if (updateAllCurrencyExchangeRates.fulfilled.match(result)) {
        const updated = result.payload;
        GlobalService.showToast(
          "success", 
          "Success", 
          `Updated ${updated.length} currency exchange rates`
        );
        
        // Refresh currencies list with current search
        const queryParams: any = {
          page: currentPage,
          limit: itemsPerPage
        };
        if (debouncedSearchQuery.trim()) {
          queryParams.name = debouncedSearchQuery.trim();
        }
        dispatch(fetchCurrencies(queryParams));
        checkApiStatus();
      } else {
        const error = result.payload as string;
        GlobalService.showToast("error", "Error", error || "Failed to update exchange rates");
      }
    } catch (error: any) {
      console.error("Failed to update exchange rates:", error);
      GlobalService.showToast("error", "Error", "Failed to update exchange rates");
    } finally {
      setUpdatingAllRates(false);
    }
  };

  // Handle currency actions
  const handleEditCurrency = (currency: Currency) => {
    setSelectedCurrency(currency);
    setModalMode("edit");
    setShowEditModal(true);
  };

  const handleCreateCurrency = () => {
    setSelectedCurrency(null);
    setModalMode("create");
    setShowEditModal(true);
  };

  const handleDeleteCurrency = async (currencyId: string) => {
    if (window.confirm("Are you sure you want to delete this currency?")) {
      dispatch(deleteCurrency(currencyId));
    }
  };

  const handleModalClose = () => {
    setShowEditModal(false);
    setSelectedCurrency(null);
    
    // Refresh currencies list when modal closes (in case of create/update)
    const queryParams: any = {
      page: currentPage,
      limit: itemsPerPage
    };
    if (debouncedSearchQuery.trim()) {
      queryParams.name = debouncedSearchQuery.trim();
    }
    dispatch(fetchCurrencies(queryParams));
  };

  // Clear search query
  const handleClearSearch = () => {
    setSearchQuery("");
    setDebouncedSearchQuery("");
    setCurrentPage(1);
  };

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Apply client-side region filtering (backend doesn't support region filter)
  // Server already handles search and pagination
  const filteredCurrencies = selectedRegion === "all"
    ? currencies
    : currencies.filter(currency => {
        const hasRegion = currency.regions?.some(region => region.id === selectedRegion);
        return hasRegion;
      });

  // Use the filtered currencies for display
  const paginatedCurrencies = filteredCurrencies;

  // Calculate total pages based on server pagination data
  const totalPages = Math.ceil((totalCount || 0) / itemsPerPage);

  const CurrencyCard = ({ currency }: { currency: Currency }) => (
    <Card className="p-6">
      <div className="flex justify-between items-start"   style={{ textAlign: "left" }}>
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-100">
            <DollarSign className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-lg">{currency.name}</h3>
              {currency.isDefault && (
                <Badge variant={undefined} className="bg-blue-100 text-blue-700">Default</Badge>
              )}
            </div>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline">{currency.initial}</Badge>
              <Badge variant={undefined} className={currency.status?.name === "Active" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}>
                {currency.status?.name?.toUpperCase() || "UNKNOWN"}
              </Badge>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => handleEditCurrency(currency)}>
            <Edit className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteCurrency(currency.id)}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Exchange Rate Section */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <div className="flex justify-between items-center">
          <div>
            <span className="text-sm text-gray-600">Exchange Rate (Base: EUR)</span>
            <p className="text-xl font-semibold text-blue-700">{currency.exchangeRate || 1.0}</p>
            <p className="text-xs text-gray-500 mt-1">1 EUR = {currency.exchangeRate || 1.0} {currency.initial}</p>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            className="flex items-center gap-2"
            onClick={() => handleUpdateRate(currency.id)}
            disabled={updatingRates[currency.id]}
          >
            {updatingRates[currency.id] ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            {updatingRates[currency.id] ? "Updating..." : "Update Rate"}
          </Button>
        </div>
      </div>

      {/* Commission Settings */}
      <div className="mt-6 grid grid-cols-2 gap-4">
        <div className="p-4 bg-gray-50 rounded-lg">
          <h4 className="font-medium mb-2">Regular Commission</h4>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Base Rate</span>
              <span className="font-medium">{currency.regularCommission || 0}%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Commission Rate</span>
              <span className="font-medium">{currency.regularCommissionRate || 0}%</span>
            </div>
          </div>
        </div>
        <div className="p-4 bg-gray-50 rounded-lg">
          <h4 className="font-medium mb-2">Emergency Commission</h4>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Nighttime</span>
              <span className="font-medium">{currency.emergencyNightCommission || 0}%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Holiday</span>
              <span className="font-medium">{currency.emergencyHolidayCommission || 0}%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Commission Rate</span>
              <span className="font-medium">{currency.emergencyCommissionRate || 0}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Fee Structure */}
      <div className="mt-6">
        <h4 className="font-medium mb-3">Fee Structure</h4>
        <div className="grid grid-cols-3 gap-4">
          <div className="p-3 bg-gray-50 rounded-lg">
            <span className="text-sm text-gray-500">Transaction Fee</span>
            <p className="font-medium mt-1">{currency.transactionFee || 0}%</p>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg">
            <span className="text-sm text-gray-500">Platform Fee</span>
            <p className="font-medium mt-1">{currency.platformFee || 0}%</p>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg">
            <span className="text-sm text-gray-500">Emergency Fee</span>
            <p className="font-medium mt-1">{currency.emergencyFee || 0}%</p>
          </div>
        </div>
      </div>

      {/* Regional Settings */}
      <div className="mt-6">
        <h4 className="font-medium mb-3">Regions</h4>
        <div className="flex flex-wrap gap-2">
          {currency.regions?.map((region) => (
            <Badge variant={undefined} key={region.id} className="bg-purple-100 text-purple-700">
              {region.name}
            </Badge>
          )) || <span className="text-gray-500">No regions assigned</span>}
        </div>
      </div>

      <div className="mt-6 pt-4 border-t flex justify-end gap-2">
        <Button variant="outline" className="flex items-center gap-2">
          <Settings className="h-4 w-4" />
          Format Settings
        </Button>
        <Button className="flex items-center gap-2">
          <ChevronRight className="h-4 w-4" />
          View Details
        </Button>
      </div>
    </Card>
  );



  return (
    <div className="min-h-screen bg-gray-50"   style={{ textAlign: "left" }}>
      <AdminHeader />
      
      <main className="lg:pl-64 pt-16">
        <div className="p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Currency Settings</h1>
                <p className="text-gray-600 mt-1">Manage currencies, exchange rates, and payment settings</p>
                {apiStatus && (
                  <div className="mt-2 flex items-center gap-2">
                    <Badge variant={apiStatus.isConfigured ? "default" : "destructive"} className="text-xs">
                      {apiStatus.isConfigured ? "✓ API Connected" : "✗ API Not Configured"}
                    </Badge>
                    {apiStatus.lastUpdate && (
                      <span className="text-xs text-gray-500">
                        Last update: {new Date(apiStatus.lastUpdate).toLocaleString()}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="flex items-center gap-2"
                  onClick={handleUpdateAllRates}
                  disabled={updatingAllRates || !apiStatus?.isConfigured}
                >
                  {updatingAllRates ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  {updatingAllRates ? "Updating All..." : "Update All Rates"}
                </Button>
                <Button className="flex items-center gap-2" onClick={handleCreateCurrency}>
                  <Plus className="h-4 w-4" />
                  Add Currency
                </Button>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Active Currencies</p>
                    <p className="text-2xl font-semibold mt-1">{currencies.length}</p>
                  </div>
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <DollarSign className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Active Regions</p>
                    <p className="text-2xl font-semibold mt-1">{regionSettings.length}</p>
                  </div>
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Globe className="h-5 w-5 text-green-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Last Rate Update</p>
                    <p className="text-2xl font-semibold mt-1">2h ago</p>
                  </div>
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Clock className="h-5 w-5 text-yellow-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Total Transactions</p>
                    <p className="text-2xl font-semibold mt-1">15.2k</p>
                  </div>
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <BarChart2 className="h-5 w-5 text-purple-600" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Search and Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search currencies by name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-10 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {searchQuery && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    title="Clear search"
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}
                {searchQuery && searchQuery !== debouncedSearchQuery && (
                  <div className="absolute right-10 top-1/2 -translate-y-1/2">
                    <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <select
                  value={selectedRegion}
                  onChange={(e) => setSelectedRegion(e.target.value)}
                  className="px-4 py-2 border rounded-lg"
                >
                  <option value="all">All Regions</option>
                  {regionSettings.map((region) => (
                    <option key={region.id} value={region.id}>
                      {region.name}
                    </option>
                  ))}
                </select>
                <Button
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Filter className="h-5 w-5" />
                  Filters
                </Button>
              </div>
            </div>

            {/* Currency Cards */}
            <div className="grid grid-cols-1 gap-6">
              {loading ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">Loading currencies...</p>
                </div>
              ) : filteredCurrencies.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">
                    {debouncedSearchQuery ? "No currencies found matching your search" : "No currencies found"}
                  </p>
                </div>
              ) : (
                paginatedCurrencies.map((currency) => (
                  <CurrencyCard key={currency.id} currency={currency} />
                ))
              )}
            </div>

            {/* Pagination Controls */}
            {!loading && totalCount > 0 && (
              <Card className="p-4">
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-600">
                      Showing {Math.min(((currentPage - 1) * itemsPerPage) + 1, totalCount)} to {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} currencies
                      {debouncedSearchQuery && <span className="font-medium"> (filtered)</span>}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Items per page:</span>
                      <select
                        value={itemsPerPage}
                        onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                        className="px-2 py-1 border rounded text-sm"
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={!hasPreviousPage}
                    >
                      Previous
                    </Button>
                    
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const pageNum = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
                        if (pageNum > totalPages || pageNum < 1) return null;
                        return (
                          <Button
                            key={pageNum}
                            variant={currentPage === pageNum ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(pageNum)}
                            className="w-8 h-8 p-0"
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={!hasNextPage}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </Card>
            )}
          </div>
        </div>
      </main>

      {/* Edit Modal */}
      {showEditModal && (
        <CurrencyActionModal
          isOpen={showEditModal}
          onClose={handleModalClose}
          currency={selectedCurrency}
          mode={modalMode}
        />
      )}
    </div>
  );
};

export default CurrencySettings;